import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import {
  captureUTMAttributionPostHog,
  subscribePostHogClientReady,
  trackPageViewPostHog,
  trackScrollDepthPostHog,
  trackTimeOnPagePostHog,
} from "@/lib/posthogAnalytics";
import { extractPostHogRouteMeta } from "@/lib/posthogRouteMeta";
import { trackPageViewMetaPixel } from "@/lib/facebookPixel";
import { recordSessionPagePath } from "@/lib/sessionPageJourney";
import { getOrCaptureFirstTouchSource } from "@/lib/detectTrafficSource";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Tracks pageviews and route_change for PostHog + Meta Pixel.
 * Also handles: scroll depth (25/50/75/90/100%), time on page,
 * UTM attribution, and route slug enrichment.
 *
 * Meta Pixel: el primer PageView lo dispara `loadMetaPixel()` (en lazy loader).
 * Acá disparamos un PageView adicional en cada route change del SPA — es noop
 * si fbq no está cargado todavía.
 */

/** Paths where PostHog is fully disabled (e.g. fullscreen video reels) */
const POSTHOG_DISABLED_PATHS = ["/reels"];

const SCROLL_THRESHOLDS = [25, 50, 75, 90, 100];

export function PostHogPageview() {
  const location = useLocation();
  const { templateName } = useCurrentTemplate();
  // Geo ya resuelta (Worker → __TEMPLATE_CONFIG__ / /api/template-config).
  // La provincia llega canonicalizada: "Buenos Aires F.D." → "CABA",
  // "Cordoba" → "Córdoba", igual que la ve el operador en el admin.
  const {
    country,
    province,
    city,
    detectionMethod,
  } = useTemplateSelection();
  const isHome = location.pathname === "/";
  const isPostHogDisabled = POSTHOG_DISABLED_PATHS.includes(location.pathname);

  // Refs so cleanup closures always see current values
  const pageEnterTime = useRef<number>(Date.now());
  const scrollDepthReached = useRef<Set<number>>(new Set());
  const disabledRef = useRef<boolean>(isPostHogDisabled);
  disabledRef.current = isPostHogDisabled;

  // ── Scroll depth tracking (rAF-throttled) ───────────────────────────────
  useEffect(() => {
    if (isPostHogDisabled) return;

    scrollDepthReached.current = new Set();
    let rafId: number | null = null;

    const checkScroll = () => {
      rafId = null;
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.round((scrollTop / docHeight) * 100);
      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !scrollDepthReached.current.has(threshold)) {
          scrollDepthReached.current.add(threshold);
          trackScrollDepthPostHog(threshold);
        }
      }
    };

    const handleScroll = () => {
      if (rafId === null) rafId = requestAnimationFrame(checkScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [location.pathname, isPostHogDisabled]);

  // ── Time on page + visibilitychange ─────────────────────────────────────
  // - On route change: fire time_on_page in cleanup
  // - On tab hide: fire page_hidden with partial time (user may not come back)
  useEffect(() => {
    pageEnterTime.current = Date.now();

    const handleVisibility = () => {
      if (disabledRef.current || typeof window === "undefined" || !window.posthog) return;
      const seconds = Math.round((Date.now() - pageEnterTime.current) / 1000);

      if (document.visibilityState === "hidden") {
        window.posthog.capture("page_hidden", {
          page_path: location.pathname,
          time_active_seconds: seconds,
          timestamp: new Date().toISOString(),
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (disabledRef.current) return;
      const seconds = Math.round((Date.now() - pageEnterTime.current) / 1000);
      if (seconds >= 2) trackTimeOnPagePostHog(seconds);
    };
  }, [location.pathname]);

  // ── Meta Pixel PageView en cada route change (independiente de PostHog) ──
  // Lo metemos en su propio effect: si PostHog está bloqueado por adblocker,
  // Meta Pixel debe seguir viendo el funnel de URLs.
  useEffect(() => {
    trackPageViewMetaPixel();
  }, [location.pathname, location.search]);

  // ── Session journey: freeze entry_path + path trail (before checkout) ───
  useEffect(() => {
    recordSessionPagePath(location.pathname, location.search);
    // First-touch traffic (UTM / IG / ads) on every route — upgrades weak → strong
    try {
      getOrCaptureFirstTouchSource();
    } catch {
      /* ignore */
    }
  }, [location.pathname, location.search]);

  // ── Pageview + route_change + UTM attribution ────────────────────────────
  // Esperar a `posthog:client-ready` (o `__loaded`) para que el wizard / Live events vean eventos fiables.
  useEffect(() => {
    if (isPostHogDisabled) return;

    let cancelled = false;
    const unsubscribe = subscribePostHogClientReady(() => {
      if (cancelled) return;

      const journey = recordSessionPagePath(location.pathname, location.search);
      const template = isHome ? templateName : null;
      const routeMeta = extractPostHogRouteMeta(location.pathname);
      trackPageViewPostHog(location.pathname, document.title, template, routeMeta);

      captureUTMAttributionPostHog(location.search, location.pathname);

      if (typeof window !== "undefined" && window.posthog) {
        const routePayload: Record<string, unknown> = {
          from_path: document.referrer
            ? (() => {
                try { return new URL(document.referrer).pathname; } catch { return null; }
              })()
            : null,
          to_path: location.pathname,
          entry_path: journey.entry_path,
          current_path: journey.current_path,
          page_paths: journey.page_paths.join(" › "),
          referrer_domain: journey.referrer_domain,
          hash: location.hash,
          page_title: document.title,
          timestamp: new Date().toISOString(),
        };

        if (template) routePayload.template = template;
        if (routeMeta) {
          routePayload.route_type = routeMeta.route_type;
          routePayload.slug = routeMeta.slug;
        }

        window.posthog.capture("route_change", routePayload);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // Omit templateName to avoid duplicate pageview when template settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash, isHome, isPostHogDisabled]);

  // ── template_view on "/" ─────────────────────────────────────────────────
  //
  // Este evento salía con SOLO template/path/timestamp. `country`, `province`,
  // `playback_id` y compañía llegaban por super properties que registran
  // `muxLandingSessionSuperProperties` — pero eso ocurre únicamente en
  // DualCTAVideoLanding e InvitacionExclusivaMux. Quien entraba por
  // BaseTemplate (la mayoría) nunca las registraba.
  //
  // Medido en PostHog (proyecto 422815, 90 días): de 746 template_view, 739
  // (99,1%) sin country ni playback_id. O sea que no se podía romper por país
  // ni por provincia el evento que justamente sirve para eso.
  //
  // Acá adjuntamos la geo que `useTemplateSelection` ya tiene resuelta, para
  // que el 100% de los eventos sea segmentable. `playback_id` NO se agrega:
  // vive dentro del componente de template y para BaseTemplate legítimamente
  // puede no haber video.
  useEffect(() => {
    if (isPostHogDisabled || !isHome || !templateName) return;

    let cancelled = false;
    const unsubscribe = subscribePostHogClientReady(() => {
      if (cancelled || typeof window === "undefined" || !window.posthog) return;
      window.posthog.capture("template_view", {
        template: templateName,
        path: location.pathname,
        timestamp: new Date().toISOString(),
        ...(country ? { country } : {}),
        ...(province ? { province } : {}),
        ...(city ? { city } : {}),
        ...(detectionMethod ? { detection_method: detectionMethod } : {}),
      });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHome, templateName, location.pathname, country, province, city, detectionMethod]);

  return null;
}
